import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
  Stack,
  Fade,
  Zoom,
  Grow,
  Slide,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  ArrowForward,
  Hotel,
  Apartment,
  Build,
  Star,
  People,
  Verified,
  ChevronRight,
  LocationOn,
  Favorite,
  FavoriteBorder,
  Phone,
  Email,
  WorkspacePremium,
  Security,
  Facebook,
  Instagram,
  Twitter,
  LinkedIn,
  KeyboardArrowUp,
  Close,
  Bed,
  Bathtub,
  SquareFoot,
  Pool,
  FitnessCenter,
  LocalParking,
  Wifi,
  Restaurant,
  Spa,
  Shield,
  Business, // Added Business import
} from '@mui/icons-material';

// Data
const featuredProperties = [
  {
    id: 1,
    title: 'Luxury Corporate Suite',
    type: 'Corporate',
    location: 'Mumbai',
    price: '₹15,000/night',
    originalPrice: '₹18,000/night',
    image: 'https://images.pexels.com/photos/271624/pexels-photo-271624.jpeg?auto=compress&cs=tinysrgb&w=800',
    rating: 4.9,
    reviews: 124,
    featured: true,
    beds: 3,
    baths: 3,
    sqft: 2000,
    amenities: ['Room Service', 'Conference Room', 'WiFi', 'Gym', 'Pool', 'Spa'],
    description: 'Experience luxury living in our premium corporate suite located in the heart of Mumbai. Perfect for business executives and corporate travelers seeking comfort and convenience.',
    area: 'BKC',
    agent: {
      name: 'Rahul Singh',
      avatar: 'https://randomuser.me/api/portraits/men/97.jpg',
      phone: '+91 98765 43210',
      email: 'rahul.singh@marvelous.com',
    },
  },
  {
    id: 2,
    title: 'Modern 3BR Apartment',
    type: 'Sale',
    location: 'Pune',
    price: '₹1.5 Cr',
    originalPrice: '₹1.7 Cr',
    image: 'https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=800',
    rating: 4.8,
    reviews: 89,
    featured: true,
    beds: 3,
    baths: 3,
    sqft: 1650,
    amenities: ['Pool', 'Gym', 'Club House', 'Parking', 'Security', 'Power Backup'],
    description: 'Beautiful modern apartment in Koregaon Park, Pune. Features spacious rooms, modern amenities, and excellent connectivity to major IT parks.',
    area: 'Koregaon Park',
    agent: {
      name: 'Sanjay Joshi',
      avatar: 'https://randomuser.me/api/portraits/men/22.jpg',
      phone: '+91 98765 43211',
      email: 'sanjay.joshi@marvelous.com',
    },
  },
  {
    id: 3,
    title: 'Executive Guest House',
    type: 'Rent',
    location: 'Mumbai',
    price: '₹12,000/night',
    originalPrice: '₹14,000/night',
    image: 'https://images.pexels.com/photos/2587054/pexels-photo-2587054.jpeg?auto=compress&cs=tinysrgb&w=800',
    rating: 4.7,
    reviews: 56,
    featured: false,
    beds: 2,
    baths: 2,
    sqft: 1500,
    amenities: ['Business Center', 'Gym', 'WiFi', 'Parking', 'Room Service', 'Kitchen'],
    description: 'Elegant guest house in Andheri East, ideal for corporate stays. Fully furnished with modern amenities and close to the international airport.',
    area: 'Andheri East',
    agent: {
      name: 'Pooja Malhotra',
      avatar: 'https://randomuser.me/api/portraits/women/24.jpg',
      phone: '+91 98765 43212',
      email: 'pooja.malhotra@marvelous.com',
    },
  },
  {
    id: 4,
    title: 'Luxury Villa with Pool',
    type: 'Sale',
    location: 'Pune',
    price: '₹3.2 Cr',
    originalPrice: '₹3.8 Cr',
    image: 'https://images.pexels.com/photos/2587059/pexels-photo-2587059.jpeg?auto=compress&cs=tinysrgb&w=800',
    rating: 5.0,
    reviews: 34,
    featured: true,
    beds: 4,
    baths: 5,
    sqft: 3200,
    amenities: ['Pool', 'Garden', 'Gym', 'Security', 'Private Terrace', 'Modular Kitchen'],
    description: 'Stunning villa in Kalyani Nagar with private pool and garden. Perfect for luxury living with all modern amenities and premium finishes.',
    area: 'Kalyani Nagar',
    agent: {
      name: 'Rajeshwari Patil',
      avatar: 'https://randomuser.me/api/portraits/women/33.jpg',
      phone: '+91 98765 43213',
      email: 'rajeshwari.patil@marvelous.com',
    },
  },
];

const services = [
  { 
    icon: <Hotel sx={{ fontSize: 40 }} />, 
    title: 'Hospitality', 
    description: 'Guest houses & corporate stays', 
    path: '/hospitality',
    color: '#9b4d3c',
    image: 'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=600&h=400&fit=crop',
  },
  { 
    icon: <Apartment sx={{ fontSize: 40 }} />, 
    title: 'Real Estate', 
    description: 'Buy, sell & rent properties', 
    path: '/real-estate',
    color: '#d49b7b',
    image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=600&h=400&fit=crop',
  },
  { 
    icon: <Build sx={{ fontSize: 40 }} />, 
    title: 'Services', 
    description: 'Property upkeep & repairs', 
    path: '/services',
    color: '#9b4d3c',
    image: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=600&h=400&fit=crop',
  },
];

const testimonials = [
  {
    id: 1,
    name: 'Sarah Johnson',
    role: 'Property Owner',
    comment: 'Marvelous transformed my property into a lucrative investment. Their team is professional, responsive, and truly cares about their clients.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1494790108777-296d5a4e83b8?auto=format&fit=crop&w=100&h=100&q=80',
  },
  {
    id: 2,
    name: 'Michael Chen',
    role: 'Corporate Guest',
    comment: 'The corporate suite was perfect for my extended stay. Impeccable service, beautiful furnishings, and attention to every detail.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=100&h=100&q=80',
  },
  {
    id: 3,
    name: 'Emily Rodriguez',
    role: 'Real Estate Investor',
    comment: 'Found my dream investment property through Marvelous. Their expertise in the market and negotiation skills are unmatched.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=100&h=100&q=80',
  },
];

// Awards data
const awards = [
  { award: 'Best Real Estate Agency 2023', by: 'Property Awards', year: '2023' },
  { award: 'Excellence in Hospitality', by: 'Business Today', year: '2022' },
  { award: 'Top 10 Luxury Properties', by: 'Luxury Lifestyle Awards', year: '2023' },
];

// Amenities icons
const amenityIcons = {
  Pool: <Pool />,
  Gym: <FitnessCenter />,
  Parking: <LocalParking />,
  WiFi: <Wifi />,
  'Conference Room': <Business />,
  Security: <Shield />,
  Garden: <Spa />,
  'Room Service': <Restaurant />,
  'Business Center': <Business />,
  Kitchen: <Restaurant />,
  'Private Terrace': <ArrowForward />,
  'Modular Kitchen': <Restaurant />,
  'Club House': <Business />,
  'Power Backup': <Build />,
};

// Custom hook for scroll animation
const useScrollAnimation = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold]);

  return [ref, isVisible];
};

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [ref, isVisible] = useScrollAnimation();

  useEffect(() => {
    if (!isVisible) return;

    let startTime = null;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// Property Details Modal Component
const PropertyDetailsModal = ({ open, onClose, property }) => {
  if (!property) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '2px solid #9b4d3c',
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: '#9b4d3c', 
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 2,
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {property.title}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ position: 'relative', height: 300 }}>
          <CardMedia
            component="img"
            image={property.image}
            alt={property.title}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <Chip
            label={property.type}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: property.type === 'Sale' ? '#4caf50' :
                      property.type === 'Rent' ? '#2196f3' :
                      property.type === 'Corporate' ? '#ff9800' : '#9c27b0',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.9rem',
              height: 30,
            }}
          />
        </Box>

        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOn sx={{ color: '#9b4d3c', mr: 1 }} />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {property.area}, {property.location}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Star sx={{ color: '#d49b7b', mr: 1 }} />
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#d49b7b', mr: 1 }}>
                  {property.rating}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ({property.reviews} reviews)
                </Typography>
              </Box>

              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                {property.description}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Bed sx={{ color: '#9b4d3c' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Beds</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{property.beds}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Bathtub sx={{ color: '#9b4d3c' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Baths</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{property.baths}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SquareFoot sx={{ color: '#9b4d3c' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Area</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{property.sqft} sqft</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
                Amenities
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                {property.amenities.map((amenity, idx) => (
                  <Chip
                    key={idx}
                    icon={React.cloneElement(amenityIcons[amenity] || <Business />, { sx: { fontSize: 16 } })}
                    label={amenity}
                    sx={{
                      bgcolor: 'rgba(155,77,60,0.05)',
                      border: '1px solid #9b4d3c',
                      borderRadius: 2,
                      height: 36,
                      '& .MuiChip-icon': { color: '#9b4d3c' },
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [favorites, setFavorites] = useState([]);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [hoveredProperty, setHoveredProperty] = useState(null);
  const [hoveredService, setHoveredService] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const toggleFavorite = (propertyId) => {
    if (favorites.includes(propertyId)) {
      setFavorites(favorites.filter(id => id !== propertyId));
    } else {
      setFavorites([...favorites, propertyId]);
    }
  };

  const handleViewDetails = (property) => {
    setSelectedProperty(property);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProperty(null);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Section border style
  const sectionBorderStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #9b4d3c 0%, #d49b7b 50%, #e0e0e0 100%)',
    zIndex: 1,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Property Details Modal */}
      <PropertyDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        property={selectedProperty}
      />

      {/* Scroll to Top Button */}
      <Zoom in={showScrollTop}>
        <IconButton
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 30,
            right: 30,
            bgcolor: '#9b4d3c',
            color: 'white',
            width: 50,
            height: 50,
            zIndex: 1000,
            '&:hover': {
              bgcolor: '#d49b7b',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.3s',
          }}
        >
          <KeyboardArrowUp />
        </IconButton>
      </Zoom>

      {/* SECTION 1: HERO - With parallax effect */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '90vh', md: '100vh' },
          minHeight: { xs: 600, md: 700 },
          background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
          overflow: 'hidden',
        }}
      >
        {/* Animated Background Image */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.4,
            backgroundImage: 'url("https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1920&h=1080&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            animation: 'slowZoom 20s infinite alternate',
            '@keyframes slowZoom': {
              '0%': { transform: 'scale(1)' },
              '100%': { transform: 'scale(1.1)' },
            },
          }}
        />

        {/* Animated Particles Effect */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 50%, rgba(212,155,123,0.1) 0%, transparent 50%)',
            animation: 'pulse 4s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 0.3 },
              '50%': { opacity: 0.6 },
            },
          }}
        />

        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            zIndex: 3,
          }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '3rem', md: '5rem' },
                      fontWeight: 900,
                      color: 'white',
                      mb: 2,
                      lineHeight: 1,
                      animation: 'slideInLeft 1s ease-out',
                      '@keyframes slideInLeft': {
                        '0%': { transform: 'translateX(-50px)', opacity: 0 },
                        '100%': { transform: 'translateX(0)', opacity: 1 },
                      },
                    }}
                  >
                    MARVELLOUS
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: '1.5rem', md: '2.5rem' },
                      fontWeight: 400,
                      color: '#d49b7b',
                      mb: 2,
                      animation: 'slideInRight 1s ease-out 0.3s both',
                      '@keyframes slideInRight': {
                        '0%': { transform: 'translateX(50px)', opacity: 0 },
                        '100%': { transform: 'translateX(0)', opacity: 1 },
                      },
                    }}
                  >
                    Luxury Living in Mumbai & Pune
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '1.2rem',
                      color: 'rgba(255,255,255,0.8)',
                      mb: 4,
                      maxWidth: 600,
                      animation: 'fadeInUp 1s ease-out 0.6s both',
                      '@keyframes fadeInUp': {
                        '0%': { transform: 'translateY(30px)', opacity: 0 },
                        '100%': { transform: 'translateY(0)', opacity: 1 },
                      },
                    }}
                  >
                    Discover an exclusive collection of premium properties curated for those who appreciate the finest things in life.
                  </Typography>
                  
                  <Zoom in={true} style={{ transitionDelay: '900ms' }}>
                    <Button
                      component={Link}
                      to="/contact"
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForward />}
                      sx={{
                        bgcolor: '#d49b7b',
                        color: '#0a0a0a',
                        py: 2,
                        px: 6,
                        fontSize: '1.2rem',
                        fontWeight: 700,
                        borderRadius: 2,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                          transition: 'left 0.5s',
                        },
                        '&:hover': {
                          bgcolor: '#e5b8a0',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 10px 20px rgba(0,0,0,0.3)',
                          '&::before': {
                            left: '100%',
                          },
                        },
                      }}
                    >
                      Contact Us
                    </Button>
                  </Zoom>
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={5}>
              <Fade in={true} timeout={1500}>
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  <Paper
                    sx={{
                      p: 4,
                      bgcolor: 'rgba(10,10,10,0.7)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 4,
                      border: '1px solid rgba(212,155,123,0.2)',
                      animation: 'float 3s ease-in-out infinite',
                      '@keyframes float': {
                        '0%, 100%': { transform: 'translateY(0)' },
                        '50%': { transform: 'translateY(-10px)' },
                      },
                    }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#d49b7b' }}>
                      Why Choose Us
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {[
                        { icon: <Verified />, text: '15+ Years Excellence' },
                        { icon: <People />, text: '1000+ Happy Clients' },
                        { icon: <Apartment />, text: '500+ Luxury Properties' },
                      ].map((item, i) => (
                        <Grow in={true} timeout={1000 + i * 200} key={i}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar 
                              sx={{ 
                                bgcolor: '#9b4d3c', 
                                width: 45, 
                                height: 45,
                                animation: 'rotate 3s linear infinite',
                                '@keyframes rotate': {
                                  '0%': { transform: 'rotate(0deg)' },
                                  '100%': { transform: 'rotate(360deg)' },
                                },
                              }}
                            >
                              {item.icon}
                            </Avatar>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'white' }}>
                              {item.text}
                            </Typography>
                          </Box>
                        </Grow>
                      ))}
                    </Box>
                  </Paper>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* SECTION 2: SERVICES - Colored outer background (#f0eae4) with white cards */}
      <Box sx={{ position: 'relative', bgcolor: '#f0eae4' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: 8 }}>
          <Fade in={true} timeout={1000}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: 700, 
                  color: '#333',
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  position: 'relative',
                  display: 'inline-block',
                  pb: 2,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 80,
                    height: 3,
                    background: 'linear-gradient(90deg, #9b4d3c, #d49b7b)',
                    borderRadius: 2,
                    animation: 'expandWidth 2s ease-in-out infinite',
                    '@keyframes expandWidth': {
                      '0%, 100%': { width: 80 },
                      '50%': { width: 120 },
                    },
                  }
                }}
              >
                Our Services
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade in={true} timeout={1000 + index * 200}>
                  <Card
                    onMouseEnter={() => setHoveredService(index)}
                    onMouseLeave={() => setHoveredService(null)}
                    sx={{
                      height: '100%',
                      borderRadius: 4,
                      overflow: 'hidden',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: hoveredService === index 
                        ? '0 30px 60px rgba(155,77,60,0.3)' 
                        : '0 10px 30px rgba(0,0,0,0.05)',
                      border: '2px solid #9b4d3c',
                      position: 'relative',
                      transform: hoveredService === index ? 'translateY(-12px) scale(1.02)' : 'translateY(0) scale(1)',
                      bgcolor: '#ffffff',
                    }}
                  >
                    {/* Image Section with Overlay */}
                    <Box sx={{ position: 'relative', height: 200, overflow: 'hidden' }}>
                      <Box
                        component="img"
                        src={service.image}
                        alt={service.title}
                        className="service-image"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.6s ease',
                          transform: hoveredService === index ? 'scale(1.1)' : 'scale(1)',
                        }}
                      />
                      <Box
                        className="service-overlay"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: `linear-gradient(135deg, ${service.color} 0%, ${service.color}dd 100%)`,
                          opacity: hoveredService === index ? 0.9 : 0.7,
                          transition: 'opacity 0.4s ease',
                          mixBlendMode: 'multiply',
                        }}
                      />
                      
                      {/* Icon on Image with Animation */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 2,
                          bgcolor: 'white',
                          width: 70,
                          height: 70,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                          animation: hoveredService === index ? 'pulse 1s ease-in-out infinite' : 'none',
                          '@keyframes pulse': {
                            '0%, 100%': { transform: 'translate(-50%, -50%) scale(1)' },
                            '50%': { transform: 'translate(-50%, -50%) scale(1.1)' },
                          },
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: 'transparent',
                            color: service.color,
                            width: 50,
                            height: 50,
                          }}
                        >
                          {service.icon}
                        </Avatar>
                      </Box>
                    </Box>

                    {/* Content Section */}
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontWeight: 700, 
                          mb: 2, 
                          color: '#333',
                          fontSize: '1.75rem',
                        }}
                      >
                        {service.title}
                      </Typography>
                      
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: '#666', 
                          mb: 3, 
                          lineHeight: 1.8,
                          minHeight: 60,
                        }}
                      >
                        {service.description}
                      </Typography>

                      <Button
                        component={Link}
                        to={service.path}
                        variant="outlined"
                        endIcon={<ArrowForward sx={{ animation: hoveredService === index ? 'moveRight 0.5s infinite' : 'none' }} />}
                        sx={{
                          borderColor: service.color,
                          color: service.color,
                          borderRadius: 30,
                          px: 4,
                          py: 1.2,
                          fontWeight: 600,
                          transition: 'all 0.3s',
                          '&:hover': {
                            bgcolor: service.color,
                            color: 'white',
                            transform: 'translateX(5px)',
                          },
                        }}
                      >
                        Learn More
                      </Button>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* SECTION 3: FEATURED PROPERTIES - REMOVED EXCLUSIVE COLLECTION AND VIEW ALL */}
      <Box sx={{ position: 'relative', bgcolor: '#f0eae4' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: 8 }}>
          <Fade in={true} timeout={1000}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: 600,
                  color: '#333',
                  fontSize: { xs: '2.2rem', md: '2.8rem' },
                  position: 'relative',
                  display: 'inline-block',
                  pb: 2,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 80,
                    height: 3,
                    background: 'linear-gradient(90deg, #9b4d3c, #d49b7b)',
                    borderRadius: 2,
                    animation: 'pulseWidth 2s ease-in-out infinite',
                    '@keyframes pulseWidth': {
                      '0%, 100%': { width: 80 },
                      '50%': { width: 120 },
                    },
                  }
                }}
              >
                Featured Properties
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={4}>
            {featuredProperties.map((property, index) => (
              <Grid item xs={12} sm={6} md={3} key={property.id}>
                <Grow in={true} timeout={1000 + index * 100}>
                  <Card
                    onMouseEnter={() => setHoveredProperty(property.id)}
                    onMouseLeave={() => setHoveredProperty(null)}
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      overflow: 'hidden',
                      transition: 'transform 0.3s, box-shadow 0.3s, border-color 0.3s',
                      boxShadow: hoveredProperty === property.id 
                        ? '0 20px 40px rgba(155,77,60,0.2)' 
                        : '0 5px 20px rgba(0,0,0,0.05)',
                      border: '2px solid #9b4d3c',
                      position: 'relative',
                      transform: hoveredProperty === property.id ? 'translateY(-8px)' : 'translateY(0)',
                      bgcolor: '#ffffff',
                    }}
                  >
                    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={property.image}
                        alt={property.title}
                        sx={{
                          transition: 'transform 0.5s',
                          transform: hoveredProperty === property.id ? 'scale(1.08)' : 'scale(1)',
                        }}
                      />
                      {property.featured && (
                        <Chip
                          label="FEATURED"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 12,
                            left: 12,
                            bgcolor: '#9b4d3c',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 24,
                          }}
                        />
                      )}
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          bgcolor: 'white',
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: '#f0f0f0',
                            transform: 'scale(1.1)',
                          },
                        }}
                        onClick={() => toggleFavorite(property.id)}
                      >
                        {favorites.includes(property.id) ? (
                          <Favorite sx={{ color: '#9b4d3c', fontSize: 20 }} />
                        ) : (
                          <FavoriteBorder sx={{ fontSize: 20 }} />
                        )}
                      </IconButton>
                    </Box>

                    <CardContent sx={{ p: 2.5 }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 600, 
                          mb: 1, 
                          color: '#333',
                          fontSize: '1.1rem',
                        }}
                      >
                        {property.title}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LocationOn sx={{ fontSize: 16, color: '#9b4d3c', mr: 0.5 }} />
                        <Typography variant="body2" color="text.secondary">
                          {property.location}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Star sx={{ fontSize: 16, color: '#d49b7b', mr: 0.5 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#d49b7b', mr: 1 }}>
                          {property.rating}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ({property.reviews} reviews)
                        </Typography>
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => handleViewDetails(property)}
                        sx={{
                          bgcolor: '#9b4d3c',
                          color: 'white',
                          py: 1,
                          borderRadius: 2,
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                            transition: 'left 0.5s',
                          },
                          '&:hover': {
                            bgcolor: '#7a3b2e',
                            '&::before': {
                              left: '100%',
                            },
                          },
                        }}
                      >
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* SECTION 4: OUR IMPACT - Same style as Services (colored outer background, white cards) */}
      <Box sx={{ position: 'relative', bgcolor: '#f0eae4' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: 8 }}>
          <Fade in={true} timeout={1000}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: 600,
                  color: '#333',
                  fontSize: { xs: '2.2rem', md: '2.8rem' },
                  position: 'relative',
                  display: 'inline-block',
                  pb: 2,
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 80,
                    height: 3,
                    background: 'linear-gradient(90deg, #9b4d3c, #d49b7b)',
                    borderRadius: 2,
                  },
                }}
              >
                OUR IMPACT
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={3}>
            {[
              { number: 500, label: 'Luxury Properties', gradient: 'linear-gradient(135deg, #9b4d3c, #b86b5a)', suffix: '+' },
              { number: 1000, label: 'Happy Clients', gradient: 'linear-gradient(135deg, #d49b7b, #e5b8a0)', suffix: '+' },
              { number: 4.9, label: 'Client Rating', gradient: 'linear-gradient(135deg, #9b4d3c, #b86b5a)', suffix: '' },
              { number: 15, label: 'Years Experience', gradient: 'linear-gradient(135deg, #d49b7b, #e5b8a0)', suffix: '+' },
            ].map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Zoom in={true} timeout={1000 + index * 200}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      background: stat.gradient,
                      borderRadius: 3,
                      height: '100%',
                      transition: 'all 0.3s',
                      border: '2px solid #9b4d3c',
                      animation: 'float 3s ease-in-out infinite',
                      animationDelay: `${index * 0.5}s`,
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 20px 40px rgba(155,77,60,0.3)',
                      },
                    }}
                  >
                    <Typography 
                      variant="h2" 
                      sx={{ 
                        fontWeight: 700, 
                        fontSize: { xs: '2.8rem', md: '3.2rem' }, 
                        lineHeight: 1.2, 
                        color: 'white',
                        mb: 1,
                        textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
                      }}
                    >
                      <AnimatedCounter end={stat.number} suffix={stat.suffix} />
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 500, 
                        color: 'rgba(255,255,255,0.9)',
                        fontSize: '1rem',
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Paper>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* SECTION 5: AWARDS & RECOGNITION - Same style as Services (colored outer background, white cards) */}
      <Box sx={{ position: 'relative', bgcolor: '#f0eae4' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="xl" sx={{ py: 8 }}>
          <Fade in={true} timeout={1000}>
            <Typography 
              variant="h2" 
              align="center" 
              sx={{ 
                fontWeight: 600,
                color: '#333', 
                mb: 6,
                fontSize: { xs: '2.2rem', md: '2.8rem' },
                position: 'relative',
                display: 'inline-block',
                width: '100%',
                pb: 2,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 80,
                  height: 3,
                  background: 'linear-gradient(90deg, #9b4d3c, #d49b7b)',
                  borderRadius: 2,
                }
              }}
            >
              Awards & Recognition
            </Typography>
          </Fade>

          <Grid container spacing={4}>
            {awards.map((item, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Grow in={true} timeout={1000 + i * 200}>
                  <Paper 
                    sx={{ 
                      p: 4, 
                      textAlign: 'center',
                      borderRadius: 3,
                      transition: 'all 0.3s',
                      border: '2px solid #9b4d3c',
                      position: 'relative',
                      overflow: 'hidden',
                      bgcolor: '#ffffff',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, #9b4d3c, #d49b7b)',
                        transform: 'translateX(-100%)',
                        transition: 'transform 0.3s',
                      },
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(155,77,60,0.15)',
                        borderColor: '#d49b7b',
                        '&::before': {
                          transform: 'translateX(0)',
                        },
                      },
                    }}
                  >
                    <WorkspacePremium 
                      className="award-icon"
                      sx={{ 
                        fontSize: 50, 
                        color: '#9b4d3c', 
                        mb: 2,
                        transition: 'all 0.3s',
                      }} 
                    />
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>{item.award}</Typography>
                    <Typography color="text.secondary" variant="body2">{item.by} • {item.year}</Typography>
                  </Paper>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* SECTION 6: TESTIMONIALS - Same style as Services (colored outer background, white cards) */}
      <Box sx={{ position: 'relative', bgcolor: '#f0eae4' }}>
        <Box sx={sectionBorderStyle} />
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Fade in={true} timeout={1000}>
            <Typography 
              variant="h2" 
              align="center" 
              sx={{ 
                fontWeight: 600,
                color: '#333', 
                mb: 6,
                fontSize: { xs: '2.2rem', md: '2.8rem' },
                position: 'relative',
                display: 'inline-block',
                width: '100%',
                pb: 2,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 80,
                  height: 3,
                  background: 'linear-gradient(90deg, #9b4d3c, #d49b7b)',
                  borderRadius: 2,
                }
              }}
            >
              What Our Clients Say
            </Typography>
          </Fade>

          <Fade in={true} timeout={1500} key={currentTestimonial}>
            <Paper 
              sx={{ 
                p: 4, 
                borderRadius: 3,
                boxShadow: '0 10px 30px rgba(155,77,60,0.1)',
                bgcolor: 'white',
                border: '2px solid #9b4d3c',
              }}
            >
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <Avatar
                    src={testimonials[currentTestimonial].image}
                    sx={{ 
                      width: 100, 
                      height: 100, 
                      mx: 'auto',
                      border: '3px solid',
                      borderColor: '#9b4d3c',
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 2, 
                      fontStyle: 'italic', 
                      color: '#555',
                      lineHeight: 1.7,
                    }}
                  >
                    "{testimonials[currentTestimonial].comment}"
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600, 
                      color: '#9b4d3c',
                      mb: 0.5,
                    }}
                  >
                    {testimonials[currentTestimonial].name}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#d49b7b', 
                      mb: 1,
                    }}
                  >
                    {testimonials[currentTestimonial].role}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} sx={{ color: '#d49b7b', fontSize: 18 }} />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Fade>

          {/* Navigation Dots */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 1 }}>
            {testimonials.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: index === currentTestimonial ? '#9b4d3c' : 'rgba(155,77,60,0.2)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  transform: index === currentTestimonial ? 'scale(1.3)' : 'scale(1)',
                  '&:hover': {
                    bgcolor: 'rgba(155,77,60,0.4)',
                  },
                }}
                onClick={() => setCurrentTestimonial(index)}
              />
            ))}
          </Box>
        </Container>
      </Box>

      {/* Global keyframes for animations */}
      <style>
        {`
          @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          @keyframes pulseWidth {
            0%, 100% { width: 80px; }
            50% { width: 120px; }
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-5px); }
          }
          
          @keyframes glow {
            0%, 100% { boxShadow: 0 0 5px rgba(212,155,123,0.3); }
            50% { boxShadow: 0 0 15px rgba(212,155,123,0.6); }
          }
          
          @keyframes heartBeat {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.2); }
          }

          @keyframes expandWidth {
            0%, 100% { width: 80px; }
            50% { width: 120px; }
          }
        `}
      </style>
    </Box>
  );
};

export default HomePage;